import { createTheme, Theme, ThemeOptions } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

// RPG rarity colors
export const rarityColors = {
  common: '#9e9e9e', // Gray
  uncommon: '#4caf50', // Green
  rare: '#2196f3', // Blue
  epic: '#9c27b0', // Purple
  legendary: '#ff9800', // Orange
  relic: '#f44336', // Red
};

// Export color palettes for use in components
export const numenorColors = {
  50: '#fdfbf7', // Lightest marble
  100: '#f8f4ec', // Warm ivory
  200: '#f0e6d2', // Aged parchment
  300: '#e6d7b7', // Light sandstone
  400: '#d4c4a0', // Weathered stone
  500: '#c1b089', // Noble bronze
  600: '#a8956f', // Aged brass
  700: '#8e7a56', // Ancient copper
  800: '#6d5e42', // Deep bronze
  900: '#504332', // Rich umber
  950: '#382f22', // Darkest stone
};

// Enhanced light mode colors for better visual comfort
export const lightModeColors = {
  // Softer, warmer whites
  background: {
    primary: '#faf9f6', // Main background - soft off-white
    secondary: '#f5f4f1', // Secondary background - slightly darker
    tertiary: '#f0ebe0', // Tertiary background - even softer
    elevated: '#fefdfb', // Elevated surfaces - very light
  },
  // Text colors with better contrast
  text: {
    primary: '#2d2a26', // Main text - soft black with warmth
    secondary: '#5a5348', // Secondary text - warm gray
    tertiary: '#7a7265', // Tertiary text - medium warm gray
    disabled: '#9a8f7f', // Disabled text - soft warm gray
  },
  // Surface colors for better hierarchy
  surface: {
    card: '#f8f6f2', // Card backgrounds
    table: '#f5f4f1', // Table backgrounds
    header: '#f0ebe0', // Header backgrounds
    sidebar: '#ebe6d9', // Sidebar backgrounds
  },
  // Border and divider colors
  borders: {
    light: '#e8e2d5', // Light borders
    medium: '#d4cbb8', // Medium borders
    dark: '#c8bda8', // Dark borders
  },
};

export const amberColors = {
  50: '#fffbeb',
  100: '#fef3c7',
  200: '#fde68a',
  300: '#fcd34d',
  400: '#fbbf24',
  500: '#f59e0b',
  600: '#d97706',
  700: '#b45309',
  800: '#92400e',
  900: '#78350f',
};

// Create theme configurations for light and dark modes based on BuildFoundry Design System
export const getDesignTokens = (mode: PaletteMode): ThemeOptions => ({
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // Light mode - Númenórean Theme with improved visual comfort
          primary: {
            main: amberColors[600], // Amber 600 for primary actions
            light: amberColors[400],
            dark: amberColors[800],
            contrastText: '#ffffff',
          },
          secondary: {
            main: numenorColors[600], // Aged brass for secondary
            light: numenorColors[400],
            dark: numenorColors[800],
            contrastText: '#ffffff',
          },
          error: {
            main: '#ef5350', // Red 400
            light: '#ffcdd2',
            dark: '#c62828',
            contrastText: '#ffffff',
          },
          warning: {
            main: '#ffa726', // Orange 400
            light: '#ffe0b2',
            dark: '#ef6c00',
            contrastText: '#ffffff',
          },
          success: {
            main: '#66bb6a', // Green 400
            light: '#c8e6c9',
            dark: '#2e7d32',
            contrastText: '#ffffff',
          },
          info: {
            main: '#42a5f5', // Blue 400
            light: '#bbdefb',
            dark: '#1976d2',
            contrastText: '#ffffff',
          },
          background: {
            default: '#faf9f6', // Softer off-white with slight warmth
            paper: '#f5f4f1', // Slightly darker paper background
          },
          text: {
            primary: '#2d2a26', // Softer black with slight warmth
            secondary: '#5a5348', // Warmer secondary text
            disabled: '#9a8f7f', // Softer disabled text
          },
          divider: '#e8e2d5', // Softer divider with warmth
          action: {
            active: numenorColors[600],
            hover: '#f0ebe0', // Softer hover state
            selected: '#e8e2d5', // Softer selection state
            disabled: numenorColors[400],
            disabledBackground: '#f8f6f2', // Softer disabled background
          },
          // Additional custom colors for better surface hierarchy
          surface: {
            main: '#faf9f6',
            light: '#fefdfb',
            dark: '#f0ebe0',
            contrast: '#2d2a26',
          },
        }
      : {
          // Dark mode - Gray Scale
          primary: {
            main: amberColors[500], // Amber 500 for primary actions
            light: amberColors[300],
            dark: amberColors[700],
            contrastText: '#000000',
          },
          secondary: {
            main: '#9ca3af', // Gray 400 for secondary
            light: '#d1d5db',
            dark: '#6b7280',
            contrastText: '#000000',
          },
          error: {
            main: '#f87171', // Red 400
            light: '#fecaca',
            dark: '#dc2626',
            contrastText: '#000000',
          },
          warning: {
            main: '#fbbf24', // Amber 400
            light: '#fde68a',
            dark: '#d97706',
            contrastText: '#000000',
          },
          success: {
            main: '#4ade80', // Green 400
            light: '#bbf7d0',
            dark: '#16a34a',
            contrastText: '#000000',
          },
          info: {
            main: '#60a5fa', // Blue 400
            light: '#bfdbfe',
            dark: '#2563eb',
            contrastText: '#000000',
          },
          background: {
            default: '#111827', // Gray 900
            paper: '#1f2937', // Gray 800
          },
          text: {
            primary: '#ffffff', // Pure white for maximum contrast
            secondary: '#ffffff', // Pure white for maximum readability
            disabled: '#e5e7eb', // Gray 200 for disabled text
          },
          divider: '#374151', // Gray 700
          action: {
            active: '#9ca3af',
            hover: '#374151',
            selected: '#4b5563',
            disabled: '#6b7280',
            disabledBackground: '#374151',
          },
        }),
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    h1: {
      fontSize: '2.5rem',
      fontWeight: 600,
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
    },
    h3: {
      fontSize: '1.75rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h6: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    subtitle1: {
      fontSize: '1rem',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '0.875rem',
      fontWeight: 500,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
    },
    body2: {
      fontSize: '0.875rem',
      fontWeight: 400,
    },
    button: {
      fontSize: '0.875rem',
      fontWeight: 500,
      textTransform: 'uppercase',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'uppercase',
          fontWeight: 500,
        } as any,
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        } as any,
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.875rem',
          fontWeight: 400,
        } as any,
        head: {
          fontWeight: 600,
        } as any,
      },
    },
    // Enhanced light mode styling
    MuiPaper: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundImage: 'none', // Remove default shadow
            border: `1px solid ${theme.palette.divider}`,
          }),
        }),
        elevation1: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          }),
        }),
        elevation2: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
            boxShadow: '0 3px 6px rgba(0,0,0,0.12), 0 1px 3px rgba(0,0,0,0.08)',
          }),
        }),
      },
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
          }),
        }),
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.default,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
          }),
        }),
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
          }),
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            '&:nth-of-type(odd)': {
              backgroundColor: theme.palette.background.default,
            },
            '&:nth-of-type(even)': {
              backgroundColor: theme.palette.background.paper,
            },
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
          }),
        }),
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
          }),
        }),
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: ({ theme }) => ({
          ...(theme.palette.mode === 'light' && {
            backgroundColor: theme.palette.background.paper,
          }),
        }),
      },
    },
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1200,
      xl: 1536,
    },
  },
});

// Function to create a theme based on the current mode
export const createAppTheme = (mode: PaletteMode): Theme => {
  return createTheme(getDesignTokens(mode));
};

export type AppTheme = Theme;
